import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Empty,
  Flex,
  Input,
  Skeleton,
  Tooltip,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import useApiV2 from "../../utils/useApiV2";
import {
  SKU_PAGE_SIZE,
  corDaVariacao,
  formatarVariacaoMoeda,
  formatarVariacaoPct,
  parametrosDaVariacao,
  proximaOrdenacao,
  textoDeModosDiferentes,
} from "./categoryImpactHelpers";

const { Text } = Typography;

const formatBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const cellStyle = { padding: "8px 16px", borderBottom: "1px solid #f0f0f0" };
const numberCell = { ...cellStyle, textAlign: "right", whiteSpace: "nowrap" };

function SortableHeader({ label, field, sort, onSort, tooltip }) {
  const active = sort.sortBy === field;
  const Icon = sort.order === "desc" ? ArrowDownOutlined : ArrowUpOutlined;
  return (
    <th
      onClick={() => onSort(field)}
      style={{
        ...cellStyle,
        background: "#fafafa",
        borderBottom: "1px solid #e8e8e8",
        color: "rgba(0,0,0,0.65)",
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 600,
        maxWidth: 0,
        overflow: "hidden",
        textAlign: "right",
        textOverflow: "ellipsis",
        userSelect: "none",
        whiteSpace: "nowrap",
      }}>
      <Tooltip title={tooltip || label}>
        <span>{label}</span>
      </Tooltip>
      <Icon
        style={{ fontSize: 11, marginLeft: 4, opacity: active ? 1 : 0.25 }}
      />
    </th>
  );
}

function ValueCells({ row, bold }) {
  const color = corDaVariacao(row.delta);
  return (
    <>
      <td style={numberCell}>{formatBRL(row.revenue_a)}</td>
      <td style={numberCell}>{formatBRL(row.revenue_b)}</td>
      <td
        style={{
          ...numberCell,
          color,
          fontWeight: bold ? 600 : 400,
        }}>
        {formatarVariacaoMoeda(row.delta)}
      </td>
      <td style={{ ...numberCell, color }}>
        {formatarVariacaoPct(row.delta_pct, row.revenue_a, row.revenue_b)}
      </td>
    </>
  );
}

function SkuRows({ applied, categoryKey, sort, search }) {
  const { getCategoryVariationSkus } = useApiV2();
  const { user } = useAuth();
  const params = parametrosDaVariacao(applied, { ...sort, search });

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: [
        "category-variation-skus-v2",
        user?.user_id ?? null,
        categoryKey,
        params,
      ],
      queryFn: ({ pageParam }) =>
        getCategoryVariationSkus({
          ...params,
          category_key: categoryKey,
          offset: pageParam,
          limit: SKU_PAGE_SIZE,
        }),
      initialPageParam: 0,
      getNextPageParam: (last, pages) => {
        const loaded = pages.reduce((n, p) => n + p.data.length, 0);
        return loaded < last.total ? loaded : undefined;
      },
    });

  const skus = data?.pages.flatMap((p) => p.data) ?? [];
  const total = data?.pages[0]?.total ?? 0;
  const childBackground = { background: "#fbfbfb" };

  if (isLoading) {
    return (
      <tr style={childBackground}>
        <td colSpan={5} style={{ ...cellStyle, paddingLeft: 40 }}>
          <Skeleton active paragraph={{ rows: 2 }} title={false} />
        </td>
      </tr>
    );
  }

  if (isError) {
    return (
      <tr style={childBackground}>
        <td colSpan={5} style={{ ...cellStyle, paddingLeft: 40 }}>
          <Text type="danger">Não foi possível carregar os produtos.</Text>
        </td>
      </tr>
    );
  }

  return (
    <>
      {skus.map((sku) => (
        <tr key={sku.produto} style={childBackground}>
          <td style={{ ...cellStyle, paddingLeft: 40 }}>
            <Flex vertical>
              <Text style={{ fontSize: 13 }}>{sku.name}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {sku.produto}
                {sku.ean ? ` · EAN ${sku.ean}` : ""}
              </Text>
            </Flex>
          </td>
          <ValueCells row={sku} />
        </tr>
      ))}
      {hasNextPage && (
        <tr style={childBackground}>
          <td colSpan={5} style={{ ...cellStyle, paddingLeft: 40 }}>
            <Button
              size="small"
              type="link"
              loading={isFetching}
              onClick={() => fetchNextPage()}>
              Mostrar mais {SKU_PAGE_SIZE} ({skus.length} de {total})
            </Button>
          </td>
        </tr>
      )}
    </>
  );
}

export default function CategoryImpactTable({ applied }) {
  const { getCategoryVariation } = useApiV2();
  const { user } = useAuth();
  const [sort, setSort] = useState({ sortBy: "delta", order: "desc" });
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setExpanded(new Set());
  }, [applied, search]);

  const params = parametrosDaVariacao(applied, { ...sort, search });
  const { data, isLoading, isError } = useQuery({
    queryKey: ["category-variation-v2", user?.user_id ?? null, params],
    queryFn: () => getCategoryVariation(params),
  });

  const rows = data?.data ?? [];
  const aviso = textoDeModosDiferentes(data?.meta);

  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const onSort = (field) => setSort((prev) => proximaOrdenacao(prev, field));

  return (
    <Flex vertical gap={12}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
        <Flex vertical>
          <Text strong>O que mais impactou a variação</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Faturamento por categoria. Clique numa categoria para ver os
            produtos.
          </Text>
        </Flex>
        <Input.Search
          allowClear
          placeholder="Buscar produto por nome, código ou EAN"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{ width: 320 }}
        />
      </Flex>

      {aviso && <Alert type="info" showIcon message={aviso} />}
      {isError && (
        <Alert
          type="error"
          showIcon
          message="Não foi possível carregar a variação por categoria."
        />
      )}

      {isLoading ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : (
        !aviso &&
        !isError &&
        (rows.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              search
                ? "Nenhum produto encontrado para essa busca."
                : "Nenhuma venda nos dois recortes."
            }
          />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                borderCollapse: "collapse",
                tableLayout: "fixed",
                width: "100%",
              }}>
              <colgroup>
                <col style={{ width: "32%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "16%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th
                    style={{
                      ...cellStyle,
                      background: "#fafafa",
                      borderBottom: "1px solid #e8e8e8",
                      color: "rgba(0,0,0,0.65)",
                      fontSize: 13,
                      textAlign: "left",
                    }}>
                    Categoria
                  </th>
                  <SortableHeader
                    label={applied.a.label}
                    field="revenue_a"
                    sort={sort}
                    onSort={onSort}
                  />
                  <SortableHeader
                    label={applied.b.label}
                    field="revenue_b"
                    sort={sort}
                    onSort={onSort}
                  />
                  <SortableHeader
                    label="Variação R$"
                    field="delta"
                    sort={sort}
                    onSort={onSort}
                    tooltip="Diferença de faturamento: B menos A"
                  />
                  <SortableHeader
                    label="Variação %"
                    field="delta_pct"
                    sort={sort}
                    onSort={onSort}
                    tooltip="Variação em relação ao faturamento de A"
                  />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const open = expanded.has(row.key);
                  const Chevron = open ? DownOutlined : RightOutlined;
                  return [
                    <tr
                      key={row.key}
                      onClick={() => toggle(row.key)}
                      style={{ cursor: "pointer" }}>
                      <td style={cellStyle}>
                        <Flex align="center" gap={8}>
                          <Chevron style={{ color: "#8c8c8c", fontSize: 11 }} />
                          <Text strong>{row.name}</Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {row.sku_count}{" "}
                            {row.sku_count === 1 ? "produto" : "produtos"}
                          </Text>
                        </Flex>
                      </td>
                      <ValueCells row={row} bold />
                    </tr>,
                    open && (
                      <SkuRows
                        key={`${row.key}-skus`}
                        applied={applied}
                        categoryKey={row.key}
                        sort={sort}
                        search={search}
                      />
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>
        ))
      )}
    </Flex>
  );
}
